import React, { useCallback, useMemo, useRef } from 'react'
import { connect } from 'react-redux'
import styled from 'styled-components'
import OfferPageGallery from '../OfferPageGallery'
import Pane from 'components/Common/Pane'
import PackageOptions from './FlashTourPackageOptions'
import { scheduleIsCurrent } from 'lib/offer/scheduleStatusUtils'
import PaneBody from 'components/Common/Pane/PaneBody'
import LayoutContainer from 'components/Common/LayoutContainer'
import ScrollAnchor from 'components/Common/ScrollAnchor'
import ShareModal from 'components/Common/Modals/ShareModal'
import OfferIntro from './FlashTourIntro'
import OfferBNPLAd from '../OfferBNPLAd'
import { getShareModalConfig } from 'lib/customer/getShareModalConfig'
import { SummaryColumn } from '../styles'
import ShareWithFriendsButton from 'components/Common/ShareWithFriendsButton'
import FlashTourSummary from './FlashTourSummary'
import { TourBookmarkButton } from 'tripPlanner/components/Bookmark/BookmarkButton'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import config from 'constants/config'
import ExpiredDealsLereRecommendations from 'components/Recommendations/ExpiredDeals/ExpiredDealsLereRecommendations'
import ImmersiveSaveButtonPortal from 'tripPlanner/components/ImmersiveHeader/ImmersiveSaveButtonPortal'
import OfferLabels from 'components/Common/Labels/OfferLabels'
import LabelGroup from 'components/Luxkit/Label/LabelGroup'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { getDefaultDepositAmountPercentage } from 'checkout/selectors/featureConfig/deposit'
import ProductPaletteProvider, { ProductPalette } from 'contexts/ProductPaletteContext'
import UltraLuxUSPs from '../UltraLux/UltraLuxUSPs'
import { isUltraLuxOffer } from 'lib/offer/offerUtils'
import { isLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'
import OfferBadgeTag from 'components/Common/OfferBadgeTag'
import PromotionLabel from 'components/Luxkit/Label/PromotionLabel'
import SolidFireIcon from 'components/Luxkit/Icons/solid/SolidFireIcon'
import ProductTypeLabel from 'components/Luxkit/Label/ProductTypeLabel'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import { useAppSelector } from 'hooks/reduxHooks'
import ClickableLink from 'components/Common/Clickable/ClickableLink'
import ExportMediaContentButton from 'components/Common/ExportMediaContentButton/ExportMediaContentButton'
import { buildAgentHubQuotingURL } from 'lib/agentHub/quoting'

const Summary = styled(Group)`
  word-break: break-word;
`

const RelativeBlock = styled.div`
  position: relative;
`

interface Props {
  customerPortalURL: string;
  memberId: string;
  offer: App.TourOffer;
  pathname: string;
  regionCode: string;
  defaultDepositAmountPercentage: number;
  cruiseLines: Array<App.CruiseLine>;
  isLuxPlusEnabled: boolean;
}

function FlashTourContent(props: Props) {
  const {
    customerPortalURL,
    memberId,
    offer,
    pathname,
    regionCode,
    defaultDepositAmountPercentage,
    cruiseLines,
    isLuxPlusEnabled,
  } = props

  const {
    id,
    images,
    locationHeading,
    locationSubheading,
    onlinePurchaseSchedule,
    packages,
  } = offer

  const packageOptionsAnchor = useRef<HTMLDivElement>(null)

  const scrollToPackageOptions = useCallback(() => {
    packageOptionsAnchor.current?.scrollIntoView()
  }, [])

  const availableForPurchase = scheduleIsCurrent(onlinePurchaseSchedule)
  const location = [locationHeading, locationSubheading].filter(t => t).join(', ')

  const shareConfig = getShareModalConfig({
    customerPortalURL,
    image: images[0],
    memberId,
    offerId: id,
    offerLocation: location,
    pathname,
    region: regionCode,
    type: 'offer',
    utmCampaign: 'webshare',
  })

  const allPrices = useMemo(() => offer.packages.map(p => p.price), [offer.packages])

  const canBookMultipleRooms = config.MULTIPLE_ROOM_BOOKING_ENABLED

  const paletteValue: ProductPalette = isLuxPlusEnabled && !!offer.luxPlus?.access ? 'loyalty' : isUltraLuxOffer(offer) ? 'ultralux' : 'default'
  const isCruiseOffer = offer.holidayTypes?.includes('Cruises')

  const region = useAppSelector(state => state.geo.currentRegionCode)
  const currency = useAppSelector(state => state.geo.currentCurrency)
  const isExportMediaEnabled = config.EXPORT_MEDIA_CONTENT_ENABLED
  const isAgentHubEnabled = config.agentHub.isEnabled
  const mediaExportURL = isAgentHubEnabled ? buildAgentHubQuotingURL(offer.id, { currency, region }) : undefined

  return (<ProductPaletteProvider palette={paletteValue}>
    <ImmersiveSaveButtonPortal>
      <TourBookmarkButton offer={offer} />
    </ImmersiveSaveButtonPortal>
    <ExpiredDealsLereRecommendations
      hideOffers={false}
    />
    <OfferPageGallery offer={offer} shareModalConfig={shareConfig} />
    <RelativeBlock>
      <Pane>
        <PaneBody size="lg-y">
          <LayoutContainer>
            <VerticalSpacer gap={12}>
              <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center">
                <LabelGroup>
                  {isCruiseOffer && <>
                    <ProductTypeLabel
                      kind="default"
                      productType="cruise-flash"
                    />
                    {!!offer?.badge && <OfferBadgeTag badge={offer.badge} />}
                    <PromotionLabel icon={<SolidFireIcon />} variant="premium">
                      Special offer
                    </PromotionLabel>
                  </>}
                  <OfferLabels offer={offer} showLimitedAvailability />
                </LabelGroup>
                <CSSBreakpoint min="tablet">
                  <Group direction="horizontal" verticalAlign="center" gap={8} horizontalAlign="end">
                    {isExportMediaEnabled && <ClickableLink target="_blank" href={mediaExportURL}>
                      <ExportMediaContentButton size="medium" />
                    </ClickableLink>}
                    <ShareModal shareConfig={shareConfig}>
                      <ShareWithFriendsButton />
                    </ShareModal>
                    <TripGuard>
                      <TourBookmarkButton offer={offer} />
                    </TripGuard>
                  </Group>
                </CSSBreakpoint>
              </Group>
              <Summary direction="horizontal" gap={16} wrap="wrap" tabletWrap="nowrap">
                <OfferIntro
                  offer={offer}
                  shareConfig={shareConfig}
                  cruiseLines={cruiseLines}
                  showPackageOptions={scrollToPackageOptions}
                />
                <SummaryColumn>
                  <FlashTourSummary
                      price={offer.lowestPricePackage.price}
                      offer={offer}
                      availableForPurchase={availableForPurchase}
                      showPackageOptions={scrollToPackageOptions}
                    />
                  <OfferBNPLAd prices={allPrices} />
                </SummaryColumn>
              </Summary>
              {offer.productType === 'ultra_lux_tour' && <UltraLuxUSPs />}
            </VerticalSpacer>
          </LayoutContainer>
        </PaneBody>
      </Pane>
      <ScrollAnchor ref={packageOptionsAnchor} />
      {!canBookMultipleRooms && packages.length > 0 && (
        <PackageOptions
          offer={offer}
          availableForPurchase={availableForPurchase}
          packages={packages}
          defaultDepositAmountPercentage={defaultDepositAmountPercentage}
        />
      )}
      {canBookMultipleRooms && (
        'MULTIPLE ROOM BOOKING IS ENABLED'
      )}
    </RelativeBlock>
  </ProductPaletteProvider>
  )
}

function mapStateToProps(state: App.State) {
  return {
    regionCode: state.geo.currentRegionCode,
    pathname: state.router.location.pathname,
    customerPortalURL: state.config.customerPortalURL,
    memberId: state.auth.account.memberId,
    defaultDepositAmountPercentage: getDefaultDepositAmountPercentage(state),
    cruiseLines: state.cruise.cruiseLines,
    isLuxPlusEnabled: isLuxPlusEnabled(state),
  }
}

export default connect(mapStateToProps)(FlashTourContent)
